import React, { useEffect, useState, useRef } from "react";
import { ChatCircle, X, PaperPlaneRight, User, Headphones } from "@phosphor-icons/react";
import { api, getUser } from "@/lib/api";
import { toast } from "sonner";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState(0);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const user = getUser();
  const isAdmin = user?.role === "admin";
  const shouldRender = !!user && !isAdmin;

  const loadMessages = async () => {
    try {
      const { data } = await api.get("/chat/messages");
      setMsgs(data.messages || []);
    } catch {}
  };

  const loadUnread = async () => {
    try {
      const { data } = await api.get("/chat/unread");
      setUnread(data.unread || 0);
    } catch {}
  };

  useEffect(() => {
    if (!shouldRender) return;
    loadUnread();
    const t = setInterval(loadUnread, 15000);
    return () => clearInterval(t);
  }, [shouldRender]);

  useEffect(() => {
    if (!shouldRender || !open) return;
    loadMessages();
    setUnread(0);
    const t = setInterval(loadMessages, 8000);
    return () => clearInterval(t);
  }, [open, shouldRender]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [msgs, open]);

  if (!shouldRender) return null;

  const send = async () => {
    const val = text.trim();
    if (!val) return;
    setSending(true);
    try {
      await api.post("/chat/message", { message: val });
      setText("");
      await loadMessages();
    } catch { toast.error("Erro ao enviar"); }
    finally { setSending(false); }
  };

  const hasNoMsgs = msgs.length === 0;

  return (
    <>
      {/* BUBBLE FLUTUANTE */}
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="chat-bubble-open"
          className="fixed z-[95] bottom-6 right-6 w-14 h-14 rounded-full bg-[var(--accent)] text-black shadow-2xl flex items-center justify-center hover:scale-110 transition-transform"
          style={{ boxShadow: "0 8px 32px rgba(234,179,8,0.4)" }}
          aria-label="Abrir chat">
          <ChatCircle size={26} weight="fill" />
          {unread > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 bg-[var(--danger)] rounded-full text-white text-[10px] font-bold flex items-center justify-center animate-pulse">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      )}

      {/* PAINEL DE CHAT */}
      {open && (
        <div className="fixed z-[95] bottom-6 right-6 w-[92vw] max-w-[380px] h-[70vh] max-h-[560px] bg-[var(--bg-base)] border border-[var(--border)] rounded-lg shadow-2xl flex flex-col overflow-hidden fadeUp" data-testid="chat-panel">
          {/* Header */}
          <div className="px-4 py-3 bg-gradient-to-r from-[var(--accent)]/20 to-transparent border-b border-[var(--border)] flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[var(--accent)] flex items-center justify-center shrink-0">
              <Headphones size={16} weight="fill" color="black" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm">Suporte MercadoPagu</div>
              <div className="text-[10px] text-[var(--success)] flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--success)] animate-pulse" />
                Respondemos em minutos
              </div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-close" className="btn-ghost !p-1.5">
              <X size={16} />
            </button>
          </div>

          {/* Mensagens */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {hasNoMsgs && (
              <div className="text-center py-6" data-testid="chat-welcome">
                <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-[var(--accent)]/10 border border-[var(--accent)]/30 flex items-center justify-center">
                  <Headphones size={24} color="var(--accent)" weight="duotone" />
                </div>
                <div className="font-display font-bold text-base mb-1">Olá, {user.name?.split(" ")[0] || "cliente"}! 👋</div>
                <div className="text-sm text-white/70 mb-4">O que posso ajudar?</div>
                <div className="grid gap-2 text-xs text-white/50">
                  <div className="border border-[var(--border)] rounded-sm p-2 text-left">💰 Dúvida sobre plano ou pagamento</div>
                  <div className="border border-[var(--border)] rounded-sm p-2 text-left">🔧 Problema técnico com integração</div>
                  <div className="border border-[var(--border)] rounded-sm p-2 text-left">💡 Sugestão para o sistema</div>
                </div>
              </div>
            )}
            {msgs.map((m) => {
              if (m.sender_role === "system") {
                return (
                  <div key={m.id} className="flex justify-center" data-testid={`chat-sys-${m.id}`}>
                    <div className="text-[10px] text-white/50 bg-white/5 border border-white/10 rounded-full px-3 py-1 text-center max-w-[85%]">
                      {m.message}
                    </div>
                  </div>
                );
              }
              const mine = m.sender_role === "client";
              return (
                <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                  <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${mine ? "bg-[var(--accent)] text-black" : "bg-white/5 text-white border border-white/10"}`}>
                    {!mine && (
                      <div className="text-[9px] font-bold text-[var(--accent)] mb-0.5 flex items-center gap-1">
                        <Headphones size={9} weight="fill" /> Suporte
                      </div>
                    )}
                    <div className="whitespace-pre-wrap break-words">{m.message}</div>
                    <div className={`text-[9px] mt-1 ${mine ? "text-black/50" : "text-white/40"}`}>
                      {new Date(m.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Input */}
          <div className="p-3 border-t border-[var(--border)] flex gap-2">
            <input value={text} onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())}
              placeholder="Digite sua mensagem..."
              data-testid="chat-input"
              className="flex-1 bg-black/40 border border-[var(--border)] rounded-sm px-3 py-2 text-sm focus:border-[var(--accent)] outline-none" />
            <button onClick={send} disabled={sending || !text.trim()} data-testid="chat-send"
              className="w-9 h-9 rounded-sm bg-[var(--accent)] text-black flex items-center justify-center disabled:opacity-40 hover:scale-105 transition">
              <PaperPlaneRight size={14} weight="fill" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
